import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  MoreHorizontal,
  User,
  MapPin,
  Globe,
  ChevronRight,
  X,
  CheckCircle,
  AlertCircle,
  Settings2,
  Menu
} from 'lucide-react';

const API_BASE_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings';

const PAGINATED_BOOKINGS_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings/viewAllBoookings';

function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    type === 'success'
      ? 'bg-[#79C485]'
      : 'bg-[#db6969]';

  const Icon =
    type === 'success'
      ? CheckCircle
      : AlertCircle;

  return (
    <div
      className={`fixed top-4 right-4 z-50 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-80`}
      style={{
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <Icon className="w-5 h-5 flex-shrink-0" />

      <span className="flex-1 text-sm font-medium">
        {message}
      </span>

      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

const getAccessToken = () =>
  sessionStorage.getItem('access_token');

const fetchAuth0Users = () => {
  const userMap = new Map();

  try {
    const storedUser = JSON.parse(
      sessionStorage.getItem('user') || '{}'
    );

    if (storedUser.email) {
      const username =
        storedUser.username ||
        storedUser.name ||
        storedUser.email.split('@')[0];

      userMap.set(storedUser.email, username);
    }
  } catch (e) {
    console.error(
      'Error reading user from sessionStorage:',
      e
    );
  }

  return userMap;
};

function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  count
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">

      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Delete {count}{' '}
            {count === 1 ? 'Booking' : 'Bookings'}?
          </h3>

          <p className="text-sm text-gray-600">
            Are you sure you want to permanently delete{' '}
            {count === 1
              ? 'this booking'
              : 'these bookings'}
            ?
            This action cannot be undone.
          </p>
        </div>

        <div className="flex gap-3 justify-end">

          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-[#db6969] rounded-md hover:bg-red-700"
          >
            Delete
          </button>

        </div>
      </div>
    </div>
  );
}

function BookingRow({
  booking,
  isSelected,
  onSelect,
  onNavigateToDetail,
  onRefresh,
  showToast
}) {
  const [showActions, setShowActions] = useState(false);

  const handleEdit = (e) => {
    e?.stopPropagation();
    onNavigateToDetail(booking.id);
  };

  const handleCancel = async (e) => {
    e?.stopPropagation();

    const action = booking.is_canceled
      ? 'reactivate'
      : 'cancel';

    const confirmMessage = booking.is_canceled
      ? 'Are you sure you want to reactivate this booking?'
      : 'Are you sure you want to cancel this booking?';

    if (!confirm(confirmMessage)) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/updateBooking`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            booking_id: booking.id,
            is_canceled: !booking.is_canceled
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
          'Failed to update booking'
        );
      }

      showToast(
        booking.is_canceled
          ? 'Booking reactivated successfully'
          : 'Booking cancelled successfully',
        'success'
      );

      onRefresh?.();

    } catch (error) {
      console.error(
        'Error updating booking:',
        error
      );

      showToast(
        `Failed to ${action} booking: ${error.message}`,
        'error'
      );
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();

    if (
      !confirm(
        'Are you sure you want to permanently delete this booking?\n\nThis action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/deleteBooking/${booking.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          'Failed to delete booking'
        );
      }

      showToast(
        'Booking deleted successfully',
        'success'
      );

      onRefresh?.();

    } catch (error) {
      console.error(
        'Error deleting booking:',
        error
      );

      showToast(
        `Failed to delete booking: ${error.message}`,
        'error'
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';

    const date = new Date(dateString);

    return date.toLocaleDateString(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'Asia/Jakarta'
      }
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    return date.toLocaleTimeString(
      'en-US',
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Jakarta'
      }
    );
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return '-';

    const diff =
      new Date(end) - new Date(start);

    const hours = Math.floor(
      diff / (1000 * 60 * 60)
    );

    const minutes = Math.floor(
      (diff % (1000 * 60 * 60)) /
      (1000 * 60)
    );

    return hours > 0
      ? `${hours}h ${minutes}m`
      : `${minutes}m`;
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'Confirmed':
        return {
          backgroundColor: '#79C485',
          color: 'white'
        };

      case 'Tentative':
        return {
          backgroundColor: '#EDB02D',
          color: 'white'
        };

      case 'Cancelled':
        return {
          backgroundColor: '#CA6F6F',
          color: 'white'
        };

      default:
        return {
          backgroundColor: '#6B7280',
          color: 'white'
        };
    }
  };

  const status =
    booking.is_canceled
      ? 'Cancelled'
      : (
        booking.is_temporary
          ? 'Tentative'
          : 'Confirmed'
      );

  const resourcePhotoUrl =
    booking.resource?.metadata?.photo_url;

  const formatCurrency = (amount) => {
    const cleanAmount =
      typeof amount === 'string'
        ? amount.replace(/[^0-9.-]+/g, '')
        : amount;

    return parseFloat(
      cleanAmount || 0
    ).toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );
  };

  return (
    <div
      className="flex items-center px-4 py-4 border-b hover:bg-gray-50 cursor-pointer transition-colors duration-150"
      style={{
        borderColor: '#D3DAE6',
        minHeight: '89px'
      }}
      onClick={() =>
        onNavigateToDetail(booking.id)
      }
      role="button"
      tabIndex={0}
    >

      <div
        className="w-8 mr-4"
        onClick={(e) =>
          e.stopPropagation()
        }
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();

            onSelect(booking.id);
          }}
          className="w-4 h-4 rounded border-gray-300"
          style={{
            accentColor: '#184AC0'
          }}
        />
      </div>

      <div
        className="w-20 h-16 mr-4 flex-shrink-0"
        onClick={(e) =>
          e.stopPropagation()
        }
      >
        <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">

          {resourcePhotoUrl ? (
            <img
              src={resourcePhotoUrl}
              alt={
                booking.resource?.name ||
                'Resource'
              }
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';

                e.target.nextElementSibling.style.display =
                  'flex';
              }}
            />
          ) : null}

          <div
            className="w-12 h-12 bg-gray-300 rounded flex items-center justify-center text-gray-600 text-xs"
            style={{
              display: resourcePhotoUrl
                ? 'none'
                : 'flex'
            }}
          >
            Room
          </div>

        </div>
      </div>

      <div className="flex-1 mr-4 min-w-0">

        <h3 className="text-sm font-semibold text-gray-900 truncate mb-1">
          {booking.resource?.name ||
            'Unknown'}{' '}
          -{' '}
          {booking.service?.name ||
            'Unknown'}
        </h3>

        <div className="flex items-center gap-1 mb-1">

          <CalendarIcon className="w-3 h-3 text-gray-400" />

          <p className="text-xs text-gray-600">
            {formatDate(
              booking.starts_at
            )}
          </p>

          <Clock className="w-3 h-3 text-gray-400 ml-2" />

          <p className="text-xs text-gray-600">
            {formatTime(
              booking.starts_at
            )}{' '}
            -{' '}
            {formatTime(
              booking.ends_at
            )}
          </p>

        </div>

        <div className="flex items-center gap-4">

          {booking.location && (
            <div className="flex items-center gap-1">

              <MapPin className="w-3 h-3 text-gray-400" />

              <p className="text-xs text-gray-500">
                {booking.location.name ||
                  'Unknown'}
              </p>

              <Globe className="w-3 h-3 text-gray-400 ml-1" />

              <p className="text-xs text-gray-500">
                {booking.location.time_zone ||
                  'UTC'}
              </p>

            </div>
          )}

          <div className="flex items-center gap-1">

            <User className="w-3 h-3 text-gray-400" />

            <p className="text-xs text-gray-500">
              {
                booking.metadata?.customer_name ||
                booking.customer_name ||
                booking.customer ||
                'Unknown'
              }
            </p>

          </div>

        </div>

      </div>

      <div className="w-32 mr-4 text-sm text-gray-900">
        {booking.price > 0
          ? `IDR ${formatCurrency(
              booking.price
            )}`
          : '-'}
      </div>

      <div className="w-24 mr-4 text-sm text-gray-900">
        {calculateDuration(
          booking.starts_at,
          booking.ends_at
        )}
      </div>

      <div className="w-24 mr-4">

        <span
          className="inline-flex px-2 py-1 text-xs font-medium rounded-full"
          style={getStatusBadgeStyle(status)}
        >
          {status}
        </span>

      </div>

      <div
        className="w-8 relative"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        <button
          className="p-1 hover:bg-gray-200 rounded"
          onClick={(e) => {
            e.stopPropagation();
            setShowActions(
              !showActions
            );
          }}
        >
          <MoreHorizontal className="w-4 h-4 text-gray-500" />
        </button>

        {showActions && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() =>
                setShowActions(false)
              }
            />

            <div className="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-40">

              <button
                className="block w-full text-left px-4 py-2 text-sm text-[#db6969] hover:bg-red-50"
                onClick={handleDelete}
              >
                Delete
              </button>

            </div>
          </>
        )}

      </div>

    </div>
  );
}

export default function BookingList({
  onAddBooking,
  refreshTrigger
}) {

  const [bookings, setBookings] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  const [searchTerm, setSearchTerm] =
    useState('');

  const [selectedBookings, setSelectedBookings] =
    useState([]);

  const [sortBy, setSortBy] =
    useState('date');

  const [sortOrder, setSortOrder] =
    useState('desc');

  const [currentPage, setCurrentPage] =
    useState(1);

  const [pageSize] =
    useState(100);

  const [totalBookings, setTotalBookings] =
    useState(0);

  const [totalPages, setTotalPages] =
    useState(1);

  const [showBulkActions, setShowBulkActions] =
    useState(false);

  const [showSortDropdown, setShowSortDropdown] =
    useState(false);

  const [statusFilter, setStatusFilter] =
    useState('all');

  const [showStatusDropdown, setShowStatusDropdown] =
    useState(false);

  const [showDeleteModal, setShowDeleteModal] =
    useState(false);

  const [isDeleting, setIsDeleting] =
    useState(false);

  const [toast, setToast] =
    useState(null);

  const navigate = useNavigate();

  const showToast = (
    message,
    type = 'success'
  ) => {
    setToast({
      message,
      type
    });
  };

  const closeToast = () => {
    setToast(null);
  };

  const fetchBookings = async (
    shouldShowToast = false,
    page = currentPage
  ) => {

    try {

      setLoading(true);
      setError(null);

      const userMap =
        await fetchAuth0Users();

      const response = await fetch(
        `${PAGINATED_BOOKINGS_URL}?page=${page}&limit=${pageSize}`,
        {
          method: 'GET',

          headers: {
            'Content-Type':
              'application/json',

            'Authorization':
              `Bearer ${getAccessToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          'Failed to fetch bookings'
        );
      }

      const result =
        await response.json();

      console.log(
        'Paginated bookings response:',
        result
      );

      const fetchedBookings =
        (result.data || []).map(
          (booking) => {

            let email =
              booking.customer_id ||
              booking.customer_name ||
              booking.metadata?.customer_name;

            if (
              booking.customer_name &&
              booking.customer_name.includes('@')
            ) {

              email =
                booking.customer_name;

            } else if (
              booking.metadata?.customer_name &&
              booking.metadata.customer_name.includes('@')
            ) {

              email =
                booking.metadata.customer_name;

            }

            const properUsername =
              email &&
              userMap.has(email)
                ? userMap.get(email)
                : (
                    booking.metadata?.customer_name ||
                    booking.customer_name ||
                    booking.customer ||
                    'Unknown'
                  );

            return {
              ...booking,

              customer_name:
                properUsername,

              customer:
                properUsername
            };

          }
        );

      setBookings(
        fetchedBookings
      );

      const total =
        result.meta?.total ??
        result.meta?.total_count ??
        result.meta?.count ??
        fetchedBookings.length;

      const pages =
        result.meta?.totalPages ??
        result.meta?.total_pages ??
        Math.ceil(
          total / pageSize
        );

      setTotalBookings(total);

      setTotalPages(
        Math.max(1, pages)
      );

      if (shouldShowToast) {

        showToast(
          'Booking created successfully!',
          'success'
        );

      }

    } catch (err) {

      console.error(
        'Error fetching bookings:',
        err
      );

      setError(
        'Failed to fetch bookings'
      );

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {

    const isRefresh =
      refreshTrigger > 0;

    fetchBookings(
      isRefresh,
      currentPage
    );

  }, [
    refreshTrigger,
    currentPage
  ]);

  const getFilteredBookings = () => {

    let filtered = bookings;

    if (
      statusFilter !== 'all'
    ) {

      filtered =
        filtered.filter(
          (booking) => {

            if (
              statusFilter ===
              'confirmed'
            ) {

              return (
                !booking.is_canceled &&
                !booking.is_temporary
              );

            } else if (
              statusFilter ===
              'tentative'
            ) {

              return (
                booking.is_temporary &&
                !booking.is_canceled
              );

            } else if (
              statusFilter ===
              'cancelled'
            ) {

              return booking.is_canceled;

            }

            return true;
          }
        );

    }

    if (!searchTerm) {
      return filtered;
    }

    const search =
      searchTerm
        .toLowerCase()
        .trim();

    return filtered.filter(
      (booking) => {

        if (
          (booking.resource?.name || '')
            .toLowerCase()
            .includes(search)
        ) {
          return true;
        }

        if (
          (booking.service?.name || '')
            .toLowerCase()
            .includes(search)
        ) {
          return true;
        }

        const customerName =
          booking.metadata?.customer_name ||
          booking.customer_name ||
          booking.customer ||
          '';

        if (
          customerName
            .toLowerCase()
            .includes(search)
        ) {
          return true;
        }

        if (
          (booking.location?.name || '')
            .toLowerCase()
            .includes(search)
        ) {
          return true;
        }

        if (
          (booking.location?.time_zone || '')
            .toLowerCase()
            .includes(search)
        ) {
          return true;
        }

        if (
          (booking.id || '')
            .toLowerCase()
            .includes(search)
        ) {
          return true;
        }

        const status =
          booking.is_canceled
            ? 'cancelled'
            : (
                booking.is_temporary
                  ? 'tentative'
                  : 'confirmed'
              );

        if (
          status.includes(search)
        ) {
          return true;
        }

        if (
          booking.price &&
          booking.price
            .toString()
            .includes(search)
        ) {
          return true;
        }

        if (booking.starts_at) {

          const date =
            new Date(
              booking.starts_at
            );

          const dateStr =
            date
              .toLocaleDateString(
                'en-US',
                {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone:
                    'Asia/Jakarta'
                }
              )
              .toLowerCase();

          if (
            dateStr.includes(search)
          ) {
            return true;
          }

          const timeStr =
            date
              .toLocaleTimeString(
                'en-US',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                  timeZone:
                    'Asia/Jakarta'
                }
              )
              .toLowerCase();

          if (
            timeStr.includes(search)
          ) {
            return true;
          }

        }

        if (
          booking.metadata?.team_members &&
          Array.isArray(
            booking.metadata.team_members
          )
        ) {

          const memberNames =
            booking.metadata.team_members
              .map(
                (m) =>
                  (
                    typeof m === 'string'
                      ? m
                      : (
                          m.name ||
                          m.username ||
                          ''
                        )
                  ).toLowerCase()
              )
              .join(' ');

          if (
            memberNames.includes(search)
          ) {
            return true;
          }

        }

        return false;

      }
    );
  };

  const getSortedBookings = (
    bookingsToSort
  ) => {

    return [...bookingsToSort]
      .sort((a, b) => {

        let aValue;
        let bValue;

        switch (sortBy) {

          case 'resource':

            aValue =
              (
                a.resource?.name ||
                ''
              ).toLowerCase();

            bValue =
              (
                b.resource?.name ||
                ''
              ).toLowerCase();

            break;

          case 'duration':

            aValue =
              a.ends_at &&
              a.starts_at
                ? new Date(
                    a.ends_at
                  ) -
                  new Date(
                    a.starts_at
                  )
                : 0;

            bValue =
              b.ends_at &&
              b.starts_at
                ? new Date(
                    b.ends_at
                  ) -
                  new Date(
                    b.starts_at
                  )
                : 0;

            break;

          case 'status':

            aValue =
              (
                a.is_canceled
                  ? 'Cancelled'
                  : (
                      a.is_temporary
                        ? 'Tentative'
                        : 'Confirmed'
                    )
              ).toLowerCase();

            bValue =
              (
                b.is_canceled
                  ? 'Cancelled'
                  : (
                      b.is_temporary
                        ? 'Tentative'
                        : 'Confirmed'
                    )
              ).toLowerCase();

            break;

          case 'date':
          default:

            aValue =
              a.created_at
                ? new Date(
                    a.created_at
                  )
                : (
                    a.starts_at
                      ? new Date(
                          a.starts_at
                        )
                      : new Date(0)
                  );

            bValue =
              b.created_at
                ? new Date(
                    b.created_at
                  )
                : (
                    b.starts_at
                      ? new Date(
                          b.starts_at
                        )
                      : new Date(0)
                  );

            break;

        }

        return sortOrder === 'asc'
          ? (
              aValue > bValue
                ? 1
                : -1
            )
          : (
              aValue < bValue
                ? 1
                : -1
            );

      });

  };

  const filteredBookings =
    getFilteredBookings();

  const sortedBookings =
    getSortedBookings(
      filteredBookings
    );

  const paginatedBookings =
    sortedBookings;

  const handleExport = () => {

    if (
      sortedBookings.length === 0
    ) {

      showToast(
        'No bookings to export',
        'error'
      );

      return;
    }

    const headers = [
      'Customer',
      'Resource',
      'Service',
      'Location',
      'Start',
      'End',
      'Price',
      'Status'
    ];

    const rows =
      sortedBookings.map(
        (booking) => {

          const customerName =
            booking.metadata?.customer_name ||
            booking.customer_name ||
            booking.customer ||
            'Unknown';

          const resourceName =
            booking.resource?.name ||
            'Unknown';

          const serviceName =
            booking.service?.name ||
            'Unknown';

          const locationName =
            booking.location?.name ||
            'Unknown';

          const status =
            booking.is_canceled
              ? 'Cancelled'
              : (
                  booking.is_temporary
                    ? 'Tentative'
                    : 'Confirmed'
                );

          const start =
            booking.starts_at
              ? new Date(
                  booking.starts_at
                ).toLocaleString(
                  'en-US',
                  {
                    timeZone:
                      'Asia/Jakarta'
                  }
                )
              : '';

          const end =
            booking.ends_at
              ? new Date(
                  booking.ends_at
                ).toLocaleString(
                  'en-US',
                  {
                    timeZone:
                      'Asia/Jakarta'
                  }
                )
              : '';

          return [
            `"${customerName}"`,
            `"${resourceName}"`,
            `"${serviceName}"`,
            `"${locationName}"`,
            `"${start}"`,
            `"${end}"`,
            booking.price || 0,
            status
          ];

        }
      );

    const csvContent =
      [
        headers.join(','),
        ...rows.map(
          (row) =>
            row.join(',')
        )
      ].join('\n');

    const blob =
      new Blob(
        [csvContent],
        {
          type:
            'text/csv;charset=utf-8;'
        }
      );

    const link =
      document.createElement('a');

    const url =
      URL.createObjectURL(
        blob
      );

    link.setAttribute(
      'href',
      url
    );

    link.setAttribute(
      'download',
      `bookings_export_${new Date()
        .toISOString()
        .split('T')[0]}.csv`
    );

    link.style.visibility =
      'hidden';

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

  };

  const handleBulkDelete =
    async () => {

      setIsDeleting(true);

      try {

        const deletePromises =
          selectedBookings.map(
            (bookingId) =>
              fetch(
                `${API_BASE_URL}/deleteBooking/${bookingId}`,
                {
                  method: 'DELETE',

                  headers: {
                    'Content-Type':
                      'application/json',

                    'Authorization':
                      `Bearer ${getAccessToken()}`,
                  },
                }
              )
          );

        const results =
          await Promise.allSettled(
            deletePromises
          );

        const successCount =
          results.filter(
            (r) =>
              r.status ===
              'fulfilled'
          ).length;

        const failCount =
          results.filter(
            (r) =>
              r.status ===
              'rejected'
          ).length;

        if (failCount > 0) {

          showToast(
            `Deleted ${successCount} bookings. Failed to delete ${failCount} bookings.`,
            'error'
          );

        } else {

          showToast(
            `Successfully deleted ${successCount} ${
              successCount === 1
                ? 'booking'
                : 'bookings'
            }`,
            'success'
          );

        }

        setSelectedBookings([]);

        await fetchBookings(
          false,
          currentPage
        );

      } catch (error) {

        console.error(
          'Error deleting bookings:',
          error
        );

        showToast(
          'Failed to delete bookings',
          'error'
        );

      } finally {

        setIsDeleting(false);

        setShowBulkActions(
          false
        );

      }

    };

  if (loading) {

    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">
          Loading...
        </div>
      </div>
    );

  }

  if (error) {

    return (
      <div className="flex justify-center py-12">
        <div className="text-[#db6969]">
          {error}
        </div>
      </div>
    );

  }

  return (
    <div className="space-y-4">

      <div className="flex items-center justify-between overflow-x-auto">

        <div className="flex items-center gap-3 flex-1">

          <label className="flex items-center w-fit gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer">

            <input
              type="checkbox"
              checked={
                selectedBookings.length ===
                  paginatedBookings.length &&
                paginatedBookings.length > 0
              }
              onChange={(e) => {

                if (
                  e.target.checked
                ) {

                  setSelectedBookings(
                    paginatedBookings.map(
                      (b) => b.id
                    )
                  );

                } else {

                  setSelectedBookings(
                    []
                  );

                }

              }}
              className="w-4 h-4 text-blue-600"
            />

            <span className="text-sm">
              Select all
            </span>

          </label>

          <div className="relative">

            <button
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() =>
                setShowSortDropdown(
                  !showSortDropdown
                )
              }
            >

              Sort:{' '}
              {sortBy.charAt(0).toUpperCase() +
                sortBy.slice(1)}{' '}

              {sortOrder === 'asc'
                ? '↑'
                : '↓'}

              <ChevronRight
                className={`w-4 h-4 transform ${
                  showSortDropdown
                    ? 'rotate-90'
                    : ''
                }`}
              />

            </button>

            {showSortDropdown && (

              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

                <div
                  className="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
                  onClick={() =>
                    setShowSortDropdown(
                      false
                    )
                  }
                />

                <div className="relative bg-white rounded-xl shadow-2xl max-w-xs w-full p-5 overflow-hidden transform transition-all text-center">

                  <div className="mb-4">

                    <h3 className="text-base font-semibold text-gray-900">
                      Sort Bookings
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      Choose how to order the list
                    </p>

                  </div>

                  <div className="flex flex-col gap-2">

                    {[
                      {
                        label:
                          'Date: Newest First',
                        value:
                          'date',
                        order:
                          'desc'
                      },
                      {
                        label:
                          'Date: Oldest First',
                        value:
                          'date',
                        order:
                          'asc'
                      },
                      {
                        label:
                          'Resource: A to Z',
                        value:
                          'resource',
                        order:
                          'asc'
                      },
                      {
                        label:
                          'Resource: Z to A',
                        value:
                          'resource',
                        order:
                          'desc'
                      },
                      {
                        label:
                          'Duration: Longest First',
                        value:
                          'duration',
                        order:
                          'desc'
                      },
                      {
                        label:
                          'Duration: Shortest First',
                        value:
                          'duration',
                        order:
                          'asc'
                      },
                      {
                        label:
                          'Status: A to Z',
                        value:
                          'status',
                        order:
                          'asc'
                      },
                      {
                        label:
                          'Status: Z to A',
                        value:
                          'status',
                        order:
                          'desc'
                      }
                    ].map(
                      (opt) => {

                        const isActive =
                          sortBy ===
                            opt.value &&
                          sortOrder ===
                            opt.order;

                        return (
                          <button
                            key={
                              opt.label
                            }
                            type="button"
                            className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors border ${
                              isActive
                                ? 'bg-blue-50 text-blue-600 border-blue-200'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
                            }`}
                            onClick={() => {

                              setSortBy(
                                opt.value
                              );

                              setSortOrder(
                                opt.order
                              );

                              setShowSortDropdown(
                                false
                              );

                            }}
                          >
                            {opt.label}
                          </button>
                        );

                      }
                    )}

                    <div className="border-t border-gray-100 my-1" />

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                      onClick={() =>
                        setShowSortDropdown(
                          false
                        )
                      }
                    >
                      Cancel
                    </button>

                  </div>

                </div>

              </div>

            )}

          </div>

          <div className="relative">

            <button
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() =>
                setShowStatusDropdown(
                  !showStatusDropdown
                )
              }
            >

              <Settings2 className="w-4 h-4" />

              Status:{' '}
              {statusFilter
                .charAt(0)
                .toUpperCase() +
                statusFilter.slice(1)}

              <ChevronRight
                className={`w-4 h-4 transform ${
                  showStatusDropdown
                    ? 'rotate-90'
                    : ''
                }`}
              />

            </button>

            {showStatusDropdown && (

              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

                <div
                  className="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
                  onClick={() =>
                    setShowStatusDropdown(
                      false
                    )
                  }
                />

                <div className="relative bg-white rounded-xl shadow-2xl max-w-xs w-full p-5 overflow-hidden transform transition-all text-center">

                  <div className="mb-4">

                    <h3 className="text-base font-semibold text-gray-900">
                      Filter by Status
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      Choose which bookings to show
                    </p>

                  </div>

                  <div className="flex flex-col gap-2">

                    {[
                      {
                        label: 'All',
                        value: 'all'
                      },
                      {
                        label:
                          'Confirmed',
                        value:
                          'confirmed'
                      },
                      {
                        label:
                          'Tentative',
                        value:
                          'tentative'
                      },
                      {
                        label:
                          'Cancelled',
                        value:
                          'cancelled'
                      }
                    ].map(
                      (opt) => {

                        const isActive =
                          statusFilter ===
                          opt.value;

                        return (
                          <button
                            key={
                              opt.value
                            }
                            type="button"
                            className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors border ${
                              isActive
                                ? 'bg-blue-50 text-blue-600 border-blue-200'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
                            }`}
                            onClick={() => {

                              setStatusFilter(
                                opt.value
                              );

                              setShowStatusDropdown(
                                false
                              );

                            }}
                          >
                            {opt.label}
                          </button>
                        );

                      }
                    )}

                    <div className="border-t border-gray-100 my-1" />

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                      onClick={() =>
                        setShowStatusDropdown(
                          false
                        )
                      }
                    >
                      Cancel
                    </button>

                  </div>

                </div>

              </div>

            )}

          </div>

          <div className="relative">

            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />

            <input
              type="text"
              placeholder="Search by resource, user, price..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(
                  e.target.value
                );

                setCurrentPage(1);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm xl:w-[300px] 2xl:w-[380px]"
            />

          </div>

        </div>

        <div className="flex items-center gap-3">

          <div className="relative">

            <button
              onClick={() =>
                setShowBulkActions(
                  !showBulkActions
                )
              }
              disabled={
                selectedBookings.length === 0
              }
              className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 ${
                selectedBookings.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >

              <Menu className="w-4 h-4" />

              <span className="text-[15px]">
                Bulk actions
              </span>

              {selectedBookings.length >
                0 && (
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {
                    selectedBookings.length
                  }
                </span>
              )}

            </button>

            {showBulkActions &&
              selectedBookings.length >
                0 && (

              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

                <div
                  className="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
                  onClick={() =>
                    setShowBulkActions(
                      false
                    )
                  }
                />

                <div className="relative bg-white rounded-xl shadow-2xl max-w-xs w-full p-5 overflow-hidden transform transition-all text-center">

                  <div className="mb-4">

                    <h3 className="text-base font-semibold text-gray-900">
                      Bulk Actions (
                      {
                        selectedBookings.length
                      }
                      )
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      Choose an action to apply to the selected records.
                    </p>

                  </div>

                  <div className="flex flex-col gap-2">

                    <button
                      type="button"
                      className="w-full px-4 py-2.5 text-sm font-medium text-white bg-[#db6969] hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                      onClick={() => {

                        setShowBulkActions(
                          false
                        );

                        setShowDeleteModal(
                          true
                        );

                      }}
                    >
                      Delete Selected
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                      onClick={() =>
                        setShowBulkActions(
                          false
                        )
                      }
                    >
                      Cancel
                    </button>

                  </div>

                </div>

              </div>

            )}

          </div>

          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >

            <Download className="w-4 h-4" />

            Export

          </button>

          <button
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md hover:bg-blue-800"
            style={{
              backgroundColor:
                '#1A5090'
            }}
            onClick={onAddBooking}
          >

            <Plus className="w-4 h-4" />

            Add booking

          </button>

        </div>

      </div>

      <div className="bg-white border rounded-lg overflow-hidden">

        <div className="overflow-x-auto">

          <div className="min-w-[900px]">

            <div className="border-b">

              <div className="flex items-center px-4 py-3 text-sm font-medium text-gray-600">

                <div
                  className="w-8 mr-4"
                  onClick={(e) =>
                    e.stopPropagation()
                  }
                />

                <div className="w-16 mr-4" />

                <div className="flex-1 mr-4">
                  Resource
                </div>

                <div className="w-32 mr-4">
                  Price
                </div>

                <div className="w-24 mr-4">
                  Duration
                </div>

                <div className="w-24 mr-4">
                  Status
                </div>

                <div className="w-8" />

              </div>

            </div>

            <div>

              {paginatedBookings.length === 0 ? (

                <div className="text-center py-12 text-gray-500">
                  No bookings found
                </div>

              ) : (

                paginatedBookings.map(
                  (booking) => (

                    <BookingRow
                      key={booking.id}
                      booking={booking}

                      isSelected={
                        selectedBookings.includes(
                          booking.id
                        )
                      }

                      onSelect={(id) =>
                        setSelectedBookings(
                          (prev) =>
                            prev.includes(id)
                              ? prev.filter(
                                  (i) =>
                                    i !== id
                                )
                              : [
                                  ...prev,
                                  id
                                ]
                        )
                      }

                      onNavigateToDetail={(id) =>
                        navigate(
                          `/bookings/${id}`
                        )
                      }

                      onRefresh={() =>
                        fetchBookings(
                          false,
                          currentPage
                        )
                      }

                      showToast={
                        showToast
                      }
                    />

                  )
                )

              )}

            </div>

          </div>

        </div>

      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4 pb-6 justify-between">

        <div className="text-sm text-gray-700">

          Showing{' '}

          {totalBookings === 0
            ? 0
            : (
                (currentPage - 1) *
                  pageSize +
                1
              )}

          {' '}to{' '}

          {Math.min(
            currentPage * pageSize,
            totalBookings
          )}

          {' '}of{' '}

          {totalBookings}

        </div>

        <div className="flex gap-2">

          <button
            onClick={() =>
              setCurrentPage(
                (prev) =>
                  Math.max(
                    1,
                    prev - 1
                  )
              )
            }
            disabled={
              currentPage === 1
            }
            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
          >
            Previous
          </button>

          {[
            ...Array(
              Math.min(
                5,
                totalPages
              )
            )
          ].map(
            (_, i) => {

              const pageNum =
                Math.max(
                  1,
                  Math.min(
                    totalPages - 4,
                    currentPage - 2
                  )
                ) + i;

              if (
                pageNum >
                totalPages
              ) {
                return null;
              }

              return (
                <button
                  key={
                    pageNum
                  }
                  onClick={() =>
                    setCurrentPage(
                      pageNum
                    )
                  }
                  className={`px-3 py-1 text-sm border rounded-md ${
                    currentPage ===
                    pageNum
                      ? 'bg-[#1A5090] text-white'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {
                    pageNum
                  }
                </button>
              );

            }
          )}

          <button
            onClick={() =>
              setCurrentPage(
                (prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
              )
            }
            disabled={
              currentPage >=
              totalPages
            }
            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
          >
            Next
          </button>

        </div>

      </div>

      <DeleteConfirmationModal
        isOpen={
          showDeleteModal
        }
        onClose={() =>
          setShowDeleteModal(
            false
          )
        }
        onConfirm={
          handleBulkDelete
        }
        count={
          selectedBookings.length
        }
      />

      {isDeleting && (

        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">

          <div className="bg-white rounded-lg p-6 shadow-xl">

            <div className="flex items-center gap-3">

              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />

              <span className="text-gray-700">
                Deleting bookings...
              </span>

            </div>

          </div>

        </div>

      )}

      {toast && (

        <Toast
          message={
            toast.message
          }
          type={
            toast.type
          }
          onClose={
            closeToast
          }
        />

      )}

    </div>
  );
}